import { useState } from 'react';

// Material UI
import { styled } from '@mui/material/styles';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import { Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import { ActionButton } from 'components/ui/buttons';

// Utility
import moment from 'moment-timezone';
import { abbreviateNumber } from 'utility/numbers';

const REVENUE_WIDTH = 72;
const ORDER_WIDTH = 72;

// Lines up the headers with the EventListRow fields
export function EventListHeader(props) {
    const { isDesktop } = props;

    if (isDesktop) {
        return (
            <Box display={'flex'} justifyContent={'flex-end'} mb={1}>
                <Typography fontWeight={'bold'} width={REVENUE_WIDTH} textAlign={'center'}>
                    Revenue
                </Typography>
                <Typography fontWeight={'bold'} width={ORDER_WIDTH} textAlign={'center'}>
                    Orders
                </Typography>
                <Box width={132} flexShrink={0} />
            </Box>
        );
    } else {
        return null;
    }
}

export function EventListRow(props) {
    const { event, isDesktop, onEventSelect, onCopy, onAddEvent, status } = props;
    const [expanded, setExpanded] = useState(true);

    const childEvents = [...(event.childEvents || [])].sort((a, b) => {
        return moment(a.start_date).unix() - moment(b.start_date).unix();
    });

    return (
        <Box mb={1}>
            <EventListRowBase
                isDesktop={isDesktop}
                isChild={false}
                event={event}
                onEventSelect={onEventSelect}
                onCopy={onCopy}
                onAddEvent={onAddEvent}
                status={status}
                expanded={expanded}
                onToggleExpanded={() => setExpanded((value) => !value)}
            />
            {expanded &&
                childEvents.map((childEvent) => (
                    <EventListRowBase
                        key={childEvent.event_id}
                        isDesktop={isDesktop}
                        isChild={true}
                        event={childEvent}
                        onEventSelect={onEventSelect}
                        onCopy={onCopy}
                        status={status}
                    />
                ))}
        </Box>
    );
}

function EventListRowBase(props) {
    const { isDesktop, isChild, onEventSelect, onCopy, onAddEvent, expanded, onToggleExpanded } =
        props;
    const e = props.event;
    const isSeason = Number(e.parent_mode) === 1;
    const seasonEventCount = e.childEvents?.length || 0;

    const [anchor, setAnchor] = useState(null);

    // Menu button callbacks need to call stopPropagation to prevent the event card from handling the click as well

    const handleMenuOpen = (ev) => {
        ev.stopPropagation();
        setAnchor(ev.currentTarget);
    };

    const handleMenuClose = (ev) => {
        ev.stopPropagation();
        setAnchor(null);
    };

    const handleCopy = (ev, eventId) => {
        ev.stopPropagation();
        onCopy(eventId);
        setAnchor(null);
    };

    const handleToggleExpanded = (ev) => {
        ev.stopPropagation();
        onToggleExpanded();
        setAnchor(null);
    };

    let publishStatus = '';

    if (e.status === 'concluded') {
        publishStatus = 'concluded';
    } else {
        if (e.live_flag === 1 && e.unlisted_flag === 0) {
            publishStatus = 'published';
        }
        if (e.live_flag === 1 && e.unlisted_flag === 1) {
            publishStatus = 'unlisted';
        }
        if (e.live_flag === 0) {
            publishStatus = 'draft';
        }
    }

    return (
        <EventContainer
            onClick={() => onEventSelect(e.event_id)}
            sx={{
                opacity: e.status === 'concluded' ? 0.5 : 1,
                marginTop: isChild ? 0.5 : 0,
                marginLeft: isChild ? 4 : 0,
            }}
        >
            <Box display="flex" flex={1} flexDirection="row" alignItems="center">
                {isDesktop && (
                    <EventImage
                        src={e.image_url}
                        ImageComponent={isChild ? ChildImage : ParentImage}
                    />
                )}
                <Box flex={1} pl={1}>
                    <Box display="flex" alignItems="center">
                        <Typography fontWeight={'bold'}>{e.event_title}</Typography>
                    </Box>
                    <EventStatus
                        startDate={moment(e.start_date).format('MMM Do YYYY')}
                        status={publishStatus}
                        isSeason={isSeason}
                        eventCount={isSeason ? seasonEventCount : null}
                    />
                    {/* {e.seats_closed_at ? <Chip size="small" label="Closed" sx={{ ml: 1 }} /> : null} */}
                </Box>
                {isDesktop && (
                    <>
                        <Typography width={REVENUE_WIDTH} textAlign={'center'}>
                            {'$' + abbreviateNumber(e.total_revenue + e.pos_total_revenue, 0, 1)}
                        </Typography>
                        <Typography width={ORDER_WIDTH} textAlign={'center'}>
                            {abbreviateNumber(e.total_orders + e.pos_total_orders, 0, 1)}
                        </Typography>
                    </>
                )}
                <ActionButton
                    size="small"
                    sx={{ minWidth: '86px', px: 1 }}
                    startIcon={isSeason ? <AddIcon /> : <FileCopyIcon />}
                    onClick={(ev) => {
                        ev.stopPropagation();
                        if (isSeason) {
                            onAddEvent(e.event_id);
                        } else {
                            onCopy(e.event_id);
                        }
                    }}
                >
                    {isSeason ? 'Event' : 'Copy'}
                </ActionButton>
                <IconButton
                    aria-label={`Actions for ${e.event_title}`}
                    onClick={handleMenuOpen}
                    size="large"
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>

            <Menu
                id={`menu-${e.event_id}`}
                anchorEl={anchor}
                keepMounted
                open={Boolean(anchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={(ev) => handleCopy(ev, e.event_id)}>
                    <FileCopyIcon color="info" />
                    <Typography ml={1}>
                        {Number(e.parent_mode) === 1 ? 'Copy Season' : 'Copy Event'}
                    </Typography>
                </MenuItem>
                {isSeason && expanded && (
                    <MenuItem onClick={handleToggleExpanded}>
                        <VisibilityOffIcon color="disabled" />
                        <Typography ml={1}>Hide Sub-Events</Typography>
                    </MenuItem>
                )}
                {isSeason && !expanded && (
                    <MenuItem onClick={handleToggleExpanded}>
                        <VisibilityIcon color="info" />
                        <Typography ml={1}>Show Sub-Events</Typography>
                    </MenuItem>
                )}
            </Menu>
        </EventContainer>
    );
}

function EventImage({ src, ImageComponent }) {
    return (
        <Box position="relative" display="inline-flex" flexShrink={0}>
            <ImageComponent src={src} />
        </Box>
    );
}

function EventStatus({ status, startDate, isSeason, eventCount }) {
    let statusText;
    let color;

    switch (status) {
        case 'concluded':
            statusText = 'Concluded';
            color = 'grey.600';
            break;
        case 'published':
            statusText = 'Published';
            color = 'success.main';
            break;
        case 'unlisted':
            statusText = 'Unlisted';
            color = 'info.main';
            break;
        case 'draft':
            statusText = 'Draft';
            color = 'warning.main';
            break;
        default:
            return null;
    }

    const seasonText =
        !isSeason || eventCount === null || eventCount === undefined
            ? ''
            : ` | Season | ${eventCount} ${eventCount === 1 ? 'event' : 'events'}`;

    return (
        <Typography variant="body2" fontWeight={'bold'} sx={{ color }}>
            {startDate} | {statusText}
            {seasonText}
        </Typography>
    );
}

const EventContainer = styled(Paper)(({ theme }) => ({
    '&:hover': {
        cursor: 'pointer',
    },
    borderRadius: '0px',
}));

const ParentImage = styled('img')({
    width: 152,
    objectFit: 'contain',
});

const ChildImage = styled('img')({
    width: 120,
    objectFit: 'contain',
});
